import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Video, Download, ChevronRight } from "lucide-react";
import SEO from "@/components/SEO";
import { trackEvent } from "@/utils/posthog";
import { useHydratedSearch } from "@/hooks/useHydratedSearch";
import { buildOrganizationJourneyUrl } from "@/utils/organizationJourney";

const WorkshopReplay = () => {
  const search = useHydratedSearch();
  const teamProgramPath = buildOrganizationJourneyUrl("/ai-30", search, { source: "workshop_replay_bottom", location: "team_program", audience: "organization" });
  const youtubeVideoId = "jOmV4GLh6BE";
  const workbookUrl = "https://ai1stplaybook.com/workshop";

  const heroContent = {
    badge: "Free Workshop Recording",
    headline: "AI Agent Skills Workshop",
    subheadline: "Build AI agents that actually work. No code required.",
    description: "Watch the Complete Workshop + Download Bonus Resources"
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="AI Agent Skills Workshop Recording | Technical Leaders"
        description="Watch the complete AI Agent Skills workshop recording plus get exclusive bonus resources. Learn to build modular AI agents with lifetime access."
        keywords={["AI agent skills", "Claude Skills", "AI automation workshop", "Claude Code", "AI workflows", "no-code AI", "AI productivity recording"]}
      />

      <Navigation />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-background via-background to-primary/5 pt-32 pb-20">
        <div className="absolute inset-0 z-0">
          <img
            src="/ai-in-ar.png"
            alt="AI background"
            className="w-full h-full object-cover object-top opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/60 to-background"></div>
        </div>
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:50px_50px]" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center z-10">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Video className="h-4 w-4" />
            {heroContent.badge}
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            <span className="bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">
              {heroContent.headline}
            </span>
          </h1>

          <p className="text-xl text-muted-foreground mb-4 max-w-2xl mx-auto">
            {heroContent.subheadline}
          </p>

          <p className="text-2xl font-semibold mb-12 max-w-2xl mx-auto">
            {heroContent.description}
          </p>

          {/* YouTube Video Embed */}
          <div className="w-full max-w-5xl mx-auto">
            <div className="relative pb-[56.25%] h-0">
              <iframe
                className="absolute top-0 left-0 w-full h-full rounded-lg shadow-2xl"
                src={`https://www.youtube.com/embed/${youtubeVideoId}`}
                title="AI Agent Skills Workshop Recording"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
          </div>

          {/* Get the Workbook Button */}
          <div className="mt-8">
            <Button
              size="lg"
              className="text-lg px-8 py-6"
              onClick={() => {
                trackEvent('Get the Workbook Clicked', {
                  location: 'hero_section'
                });
                window.open(workbookUrl, '_blank');
              }}
            >
              <Download className="mr-2 h-5 w-5" />
              Get the Workbook
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-100 dark:bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-bold text-center text-foreground mb-16 leading-tight">
            Bring the Workshop to Your Team.<br />
            Put AI to Work Together.
          </h2>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Image */}
            <div className="flex justify-center lg:justify-start">
              <img
                src="/ai-program-product-shot.jpg"
                alt="Tech Leaders AI agent curriculum and resources"
                className="w-full max-w-md rounded-2xl shadow-xl"
              />
            </div>

            {/* Right Column - Content */}
            <div className="space-y-6">
              <p className="text-xl text-muted-foreground font-medium">
                Build practical AI skills across your team in 30 days.
              </p>

              <p className="text-lg text-muted-foreground">
                Bring the workflows your people need to improve. In AI 30, your team builds, tests, and measures practical AI workflows, whether you are starting fresh or improving agents already in use.
              </p>

              <p className="text-lg text-muted-foreground">
                Eight hours of live training, with practice and implementation support throughout the 30 days. Small cohorts are welcome. We agree on the seats, priorities, and delivery format with your organization.
              </p>

              <p className="text-lg text-muted-foreground">
                Keep reusable instructions and quality checks. Your people review outputs, approve consequential actions, and decide what to improve next.
              </p>

              <div className="pt-4">
                <Button asChild size="lg" className="h-auto min-h-12 whitespace-normal bg-sky-500 px-6 py-4 text-base text-white hover:bg-sky-600">
                  <a href={teamProgramPath} onClick={() => trackEvent('AI Program CTA Clicked', {
                    location: 'workshop_replay_bottom',
                    destination: '/ai-30',
                    audience: 'organization',
                    cta_text: 'Explore AI 30 for your team',
                  })}>
                    Explore AI 30 for your team
                    <ChevronRight className="ml-2 h-5 w-5 shrink-0" aria-hidden="true" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WorkshopReplay;
